import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const DocsContainer = styled.div`
  min-height: 100vh;
  background: rgba(12, 8, 20, 1);
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  position: relative;
`;

const NavigationBar = styled.nav`
  background: rgba(20, 15, 30, 0.95);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  position: sticky;
  top: 0;
  z-index: 100;
`;

const NavigationContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 14px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    padding: 12px 20px;
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MavrkLogo = styled.img`
  width: 110px;
  height: 45px;
  object-fit: contain;
  cursor: pointer;
  opacity: 0.95;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 1;
  }
  
  @media (max-width: 768px) {
    width: 95px;
    height: 38px;
  }
`;

const DocsLabel = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  margin-left: 12px;
  padding-left: 12px;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavigationLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    gap: 4px;
  }
`;

const NavLink = styled(Link)<{ $selected: boolean }>`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${(props: { $selected: boolean }) => (props.$selected ? '#ffffff' : 'rgba(255, 255, 255, 0.65)')};
  text-decoration: none;
  transition: all 0.2s ease;
  padding: 6px 12px;
  border-radius: 6px;
  background: ${(props: { $selected: boolean }) => (props.$selected ? 'rgba(168, 85, 247, 0.1)' : 'transparent')};
  
  &:hover {
    color: #ffffff;
    background: rgba(168, 85, 247, 0.08);
  }
  
  @media (max-width: 768px) {
    font-size: 13px;
    padding: 6px 10px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AppButton = styled(Link)`
  background: linear-gradient(135deg, rgba(147, 51, 234, 0.9), rgba(168, 85, 247, 0.9));
  color: #ffffff;
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: 7px;
  padding: 7px 16px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(168, 85, 247, 0.35);
    text-decoration: none;
    color: #ffffff;
    border-color: rgba(168, 85, 247, 0.5);
  }
`;

const DocsContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 0;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.aside`
  background: rgba(20, 15, 30, 0.6);
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  padding: 32px 24px;
  height: calc(100vh - 73px);
  position: sticky;
  top: 73px;
  overflow-y: auto;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(168, 85, 247, 0.2);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(168, 85, 247, 0.35);
  }
  
  @media (max-width: 1024px) {
    position: static;
    height: auto;
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    padding: 24px 20px;
  }
`;

const SidebarTitle = styled.h3`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 24px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const SidebarSection = styled.div`
  margin-bottom: 28px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SidebarSectionTitle = styled.h4`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding-left: 12px;
`;

const SidebarLink = styled(Link)<{ $active: boolean }>`
  display: flex;
  align-items: center;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: ${(props: { $active: boolean }) => (props.$active ? '600' : '400')};
  color: ${(props: { $active: boolean }) => (props.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.65)')};
  text-decoration: none;
  padding: 7px 12px;
  border-radius: 6px;
  margin-bottom: 2px;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  background: ${(props: { $active: boolean }) => (props.$active ? 'rgba(168, 85, 247, 0.12)' : 'transparent')};
  border-left: ${(props: { $active: boolean }) => (props.$active ? '2px solid #a855f7' : '2px solid transparent')};
  
  &:hover {
    color: #ffffff;
    background: rgba(168, 85, 247, 0.08);
    text-decoration: none;
    transform: translateX(2px);
  }
`;

const MainContent = styled.main`
  background: transparent;
  padding: 48px 64px;
  min-height: calc(100vh - 73px);
  max-width: 900px;
  
  @media (max-width: 1024px) {
    padding: 40px 32px;
  }
  
  @media (max-width: 768px) {
    padding: 32px 24px;
  }
  
  @media (max-width: 480px) {
    padding: 24px 16px;
  }
`;

const DocsLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  const sidebarSections = [
    {
      title: 'Getting Started',
      links: [
        { path: '/docs', label: 'Introduction', exact: true },
        { path: '/docs/quick-start', label: 'Quick Start Guide' }
      ]
    },
    {
      title: 'How to Use',
      links: [
        { path: '/docs/mavrk-locker', label: 'Mavrk Locker' },
        { path: '/docs/token-trading', label: 'Token Trading' },
        { path: '/docs/tydro-leveraged-trading', label: 'Tydro Leveraged Trading' },
        { path: '/docs/token-locker', label: 'Token Locker System' },
        { path: '/docs/bridge-operations', label: 'Bridge Operations' },
        { path: '/docs/wallet-management', label: 'Wallet Management' }
      ]
    },
    {
      title: 'Network & Config',
      links: [
        { path: '/docs/contract-addresses', label: 'Contract Addresses' }
      ]
    },
    {
      title: 'Legal',
      links: [
        { path: '/docs/terms-and-conditions', label: 'Terms and Conditions' }
      ]
    }
  ];

  const isActive = (path: string, exact: boolean = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <DocsContainer>
      <NavigationBar>
        <NavigationContainer>
          <LogoSection>
            <Link to="/">
              <MavrkLogo
                src="/landing-assets/mavrk_word_logo.png"
                alt="Mavrk Logo"
              />
            </Link>
            <DocsLabel>Documentation</DocsLabel>
          </LogoSection>

          <NavigationLinks>
            <NavLink to="/" $selected={false}>
              Home
            </NavLink>
            <NavLink to="/explore" $selected={false}>
              App
            </NavLink>
            <NavLink to="/docs" $selected={true}>
              Docs
            </NavLink>
          </NavigationLinks>

          <ActionButtons>
            <AppButton to="/explore">
              Open App
            </AppButton>
          </ActionButtons>
        </NavigationContainer>
      </NavigationBar>

      <DocsContent>
        <Sidebar>
          <SidebarTitle>Contents</SidebarTitle>
          {sidebarSections.map((section, index) => (
            <SidebarSection key={index}>
              <SidebarSectionTitle>{section.title}</SidebarSectionTitle>
              {section.links.map((link, linkIndex) => (
                <SidebarLink
                  key={linkIndex}
                  to={link.path}
                  $active={isActive(link.path, link.exact)}
                >
                  {link.label}
                </SidebarLink>
              ))}
            </SidebarSection>
          ))}
        </Sidebar>

        <MainContent>
          {children}
        </MainContent>
      </DocsContent>
    </DocsContainer>
  );
};

export default DocsLayout;
